import React from "react";
import { useAuth } from "@/lib/auth";

/**
 * Phase 6 RBAC — conditional render wrapper driven by /auth/whoami/permissions.
 *
 * Usage:
 *   <Gated resource="payments_write">
 *     <RecordPaymentButton />
 *   </Gated>
 *
 *   <Gated resource="close_project" fallback={<span>(Owner only)</span>}>
 *     <CloseProjectButton />
 *   </Gated>
 *
 * Why a component instead of inline `{can("...") && ...}`:
 *   - One audit point for every gated UI element (grep `<Gated`).
 *   - Future cross-cutting changes (logging, tooltip, disabled-vs-hidden mode)
 *     happen in one place.
 *   - Forces explicit `resource` strings — no implicit role arrays drifting
 *     out of sync with backend `RESOURCE_GATES`.
 *
 * If `resource` is missing or returns false → renders `fallback` (default null).
 */
export default function Gated({ resource, fallback = null, children }) {
  const { can } = useAuth();
  if (!resource) return fallback;
  return can(resource) ? <>{children}</> : fallback;
}
